import {
  Component,
  Event,
  EventEmitter,
  Host,
  Listen,
  Method,
  Prop,
  State,
  h,
} from '@stencil/core';
import { Unsubscribe, store } from '@stencil/redux';
import { SfcInputTextCustomEvent } from '@sfc/components';
import { IValidationResult, VALIDITY_STATE } from '@sfc/types';
import { Constants, Language, SigningMethod } from '../../enums/const-enums';
import {
  setAccountNumber,
  setCardNumber,
  setDebitCard,
  setIsCardRemembered,
  setIsCardValid,
} from '../../redux/actions/card-action';
import { switchLanguage } from '../../redux/actions/config-actions';
import { RootState, initialStore } from '../../redux/store';
import {
  focusSignElement,
  getLocalStorageItem,
  handleBlur,
  handleEnterKey,
  isRassSignInDocument,
  logd,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '../../utils/utils';
import { getCookieValue } from '../../utils/utils';
import {
  RassCardInputTranslations,
  rassCardInputTranslations,
} from './i18n/rass-card-input.i18n';

@Component({
  tag: 'rass-card-input',
  styleUrl: 'rass-card-input.scss',
  shadow: false,
})
export class RassCardInput {
  @State() assetsPath: string;
  @Prop({ mutable: true }) language: Language;
  @State() activeSignMethod: SigningMethod;
  @State() showAccountNumberFormatError: boolean;
  @State() showCardNumberFormatError: boolean;
  @State() isCardSwitchAllowed: boolean;
  @Prop() isRememberMeEnabled = true;
  @Prop() isDisabled: boolean;
  @State() isMobileAppInd: boolean;
  accountNumber: string;
  isAccountNumberValid = false;
  cardNumber: string;
  isCardNumberValid = false;
  isCardRemembered: boolean;
  @State() isCardValid: boolean;
  @Event({ eventName: 'account:update' })
  updateAccountNumberEvent: EventEmitter;
  @Event({ eventName: 'rass:card:complete' }) cardCompleteEvent: EventEmitter;
  @Event({ eventName: 'code:reset' }) resetCodeEvent: EventEmitter;
  switchLanguage!: typeof switchLanguage;
  setAccountNumber!: typeof setAccountNumber;
  setCardNumber!: typeof setCardNumber;
  setIsCardRemembered!: typeof setIsCardRemembered;
  setIsCardValid!: typeof setIsCardValid;
  setDebitCard!: typeof setDebitCard;
  unsubscribe!: Unsubscribe;
  cardNrInputElement!: HTMLSfcInputElement;

  componentWillLoad(): void {
    store.setStore(initialStore);
    store.mapDispatchToProps(this, {
      switchLanguage,
      setAccountNumber,
      setCardNumber,
      setIsCardRemembered,
      setIsCardValid,
      setDebitCard,
    });
    if (!isRassSignInDocument()) {
      this.switchLanguage(this.language);
    }
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      const {
        config: { language },
        methods: { activeSignMethod },
        card: {
          accountNumber,
          cardNumber,
          isCardRemembered,
          isCardSwitchAllowed,
          isCardValid,
        },
      } = state;
      return {
        language,
        activeSignMethod,
        accountNumber,
        cardNumber,
        isCardRemembered,
        isCardSwitchAllowed,
        isCardValid,
      };
    });
    this.handleCardStorage();
    this.setMobileAppInd();
    if (this.isCardValid) {
      this.isAccountNumberValid = true;
      this.isCardNumberValid = true;
      this.cardCompleteEvent.emit();
    }
  }

  setMobileAppInd(): void {
    const pnpCookie = getCookieValue(Constants.APP_PNP_COOKIE_NAME);
    this.isMobileAppInd = pnpCookie != null && pnpCookie.length > 0;
  }

  componentDidLoad(): void {
    this.updateAccountNumberEvent.emit({
      accountNumber: this.accountNumber,
    });
    if (
      !this.isCardValid &&
      this.activeSignMethod !== SigningMethod.RANDOM_READER
    ) {
      focusSignElement('rass-data-reknr input', { preventScroll: true });
    }
  }

  handleCardStorage(): void {
    if (
      (this.accountNumber === '' || this.accountNumber === undefined) &&
      getLocalStorageItem('isCardRemembered') &&
      !this.isMobileAppInd &&
      initialStore.getState().card.creditCardType === null
    ) {
      logd(() => console.log('Handle card storage'));
      this.setDebitCard({
        accountNumber: getLocalStorageItem('accountNumber'),
        cardNumber: getLocalStorageItem('cardNumber'),
        isCardRemembered: true,
        isCardSwitchAllowed: true,
        isCardValid: true,
      });
    }
  }

  @Listen('account:change')
  handleChangeAccountNumber(
    event: CustomEvent<{ accountNumber: string; valid: boolean }>,
  ): void {
    this.setAccountNumber(event.detail.accountNumber);
    this.isAccountNumberValid = false;

    if (event.detail.valid) {
      this.isAccountNumberValid = true;
      if (!this.isCardNumberValid) {
        focusSignElement('rass-data-pasnr input');
      }
    }
    this.handleCardInfoChange();
  }

  handleChangeCardNumber(event: SfcInputTextCustomEvent<unknown>): void {
    this.setCardNumber((event.target as unknown as HTMLInputElement).value);
    this.isCardNumberValid = false;

    if (this.cardNumber.match(/^[0-9]{4}$/)) {
      this.isCardNumberValid = true;
      if (!this.isAccountNumberValid) {
        focusSignElement('rass-data-reknr input');
      }
    } else if (this.cardNumber.length === 4) {
      this.showCardNumberFormatError = !this.isCardNumberValid;
    }
    this.handleCardInfoChange();
  }

  handleCardInfoChange(): void {
    if (this.isAccountNumberValid && this.isCardNumberValid) {
      this.setIsCardValid(true);
      this.cardCompleteEvent.emit();
    } else if (this.isCardValid) {
      this.setIsCardValid(false);
      this.resetCodeEvent.emit();
    }
  }

  handleChangeRememberMe(): void {
    this.setIsCardRemembered(!this.isCardRemembered);
  }

  @Listen('rass:code:submit', { target: 'window' })
  onCodeSubmit(): void {
    if (
      initialStore.getState().card.isCardRemembered &&
      initialStore.getState().card.isCardSwitchAllowed
    ) {
      setLocalStorageItem(
        'accountNumber',
        initialStore.getState().card.accountNumber,
      );
      setLocalStorageItem(
        'cardNumber',
        initialStore.getState().card.cardNumber,
      );
      setLocalStorageItem('isCardRemembered', 'true');
    } else if (initialStore.getState().card.isCardSwitchAllowed) {
      removeLocalStorageItem('accountNumber');
      removeLocalStorageItem('cardNumber');
      removeLocalStorageItem('isCardRemembered');
    }
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  @Method()
  async reset(): Promise<void> {
    this.isCardNumberValid = false;
    this.setCardNumber('');
    this.isAccountNumberValid = false;
    this.setAccountNumber('');
    this.updateAccountNumberEvent.emit({
      accountNumber: this.accountNumber,
    });
    this.setIsCardValid(false);
  }

  get translations(): RassCardInputTranslations {
    return (
      rassCardInputTranslations[this.language] ||
      rassCardInputTranslations[Language.EN]
    );
  }

  addValidator(): void {
    this.cardNrInputElement
      .addValidator((): IValidationResult => {
        return this.showCardNumberFormatError ||
          (!this.isCardNumberValid && this.cardNumber.length === 4)
          ? {
              message: this.translations.card_number.format_error,
              type: VALIDITY_STATE.CUSTOM_ERROR,
            }
          : {};
      })
      .catch(() => console.warn('Unable to validate the card number.'));
  }

  handleInputBlur(): void {
    this.showCardNumberFormatError = handleBlur(
      this.cardNumber,
      this.isCardNumberValid,
    );
    this.validateInput();
  }

  handleInputKeyDown(event: SfcInputTextCustomEvent<KeyboardEvent>): void {
    this.showCardNumberFormatError = handleEnterKey(
      event.detail,
      this.isCardNumberValid,
    );
    this.validateInput();
  }

  validateInput(): void {
    this.cardNrInputElement
      .validate()
      .catch(() => console.warn('Unable to validate the card number.'));
  }

  render(): void {
    return (
      <Host
        class={
          this.isCardSwitchAllowed ? 'section-card-input' : 'sfc-display-none'
        }
      >
        <div class="sfc-form" role="form">
          <div class="sfc-row sfc-justify-content-center">
            <div class="input-div sfc-col-xs-12 sfc-col-sm-6">
              <rass-account-nr
                is-disabled={this.isDisabled}
                language={this.language}
              />
            </div>
            <div class="input-div sfc-col-xs-12 sfc-col-sm-3">
              <sfc-input
                label={this.translations.card_number.label}
                inputmode="numeric"
                max-length={4}
                name="rass-data-pasnr"
                id="rass-data-pasnr"
                value={this.cardNumber}
                disabled={this.isDisabled}
                ref={(el: HTMLSfcInputElement): HTMLSfcInputElement =>
                  (this.cardNrInputElement = el)
                }
                onSfcInput={(event): void => this.handleChangeCardNumber(event)}
                onSfcBlur={(): void => this.handleInputBlur()}
                onSfcKeyDown={(event): void => this.handleInputKeyDown(event)}
                onSfcReady={(): void => this.addValidator()}
              ></sfc-input>
            </div>
            <div
              class={
                this.isCardSwitchAllowed &&
                this.isRememberMeEnabled &&
                !this.isMobileAppInd
                  ? 'checkbox sfc-col-xs-12 sfc-col-sm-3 sfc-align-self-center'
                  : 'sfc-display-none'
              }
            >
              <sfc-checkbox
                id="rass-checkbox"
                aria-label={this.translations.checkbox.label}
                checked={this.isCardRemembered}
                disabled={this.isDisabled}
                label={this.translations.checkbox.label}
                onSfcChange={(): void => this.handleChangeRememberMe()}
              ></sfc-checkbox>
            </div>
          </div>
        </div>
      </Host>
    ) as void;
  }
}
